import { subscribeKey } from "valtio/utils";

import {
  __ONLY_WRITE_STATE_FROM_ACTIONS as writeState,
  isPersistent,
  readState,
} from "@/__main__/app-state.mjs";
import { IS_NODE, MAX_TIME } from "@/__main__/constants.mjs";
import { deserializeValue } from "@/__main__/db.mjs";
import { freshInitialState } from "@/__main__/initial-state.mjs";
import { initSettings, migrateLinkedAccounts } from "@/app/actions.mjs";
import appRefs from "@/app/refs.mjs";
import type { AppSettings } from "@/data-models/app-settings.mjs";
import type { CsgoPlayer } from "@/data-models/csgo-player.mjs";
import { updateUserPreferences } from "@/feature-auth/fetches/update-user-preferences.mjs";
import type { User } from "@/feature-auth/models/user-model.mjs";
import clone from "@/util/clone.mjs";
import deepMerge from "@/util/deep-merge.mjs";
import diff from "@/util/diff.mjs";
import subscriptionStateDiff from "@/util/subscription-state-diff.mjs";

// holds the cleanup function for settings backend persistence
export let unsubscribeSettings: (() => void) | null = null;

// create a pending promise
let resolveUserPreferenceUpdatePromise;
function resetUserPreferenceUpdatePromise() {
  appRefs.userPreferenceUpdatePromise = new Promise((resolve) => {
    resolveUserPreferenceUpdatePromise = resolve;
  });
}

/**
 * When the user changes, we apply updates to our settings. This
 * method writes to state when the user changes.
 */
export async function applyUserSettings(nextUser?: User, previousUser?: User) {
  if (previousUser?.id === nextUser?.id) return;

  unsubscribeSettings?.();

  const config = nextUser?.preferences?.config;

  if (!config) return;

  await initSettings();
  const settings = clone(readState.settings || freshInitialState.settings);

  if (config.blitzApp) {
    // merge in settings persisted from the backend
    await mergePersistedSettings(config.blitzApp, settings);
    // persist settings migrations/merges to backend compared to what was persisted
    const changedUserPreferences = diff(config.blitzApp, settings);
    if (changedUserPreferences) updateUserPreferences(changedUserPreferences);
  }

  // persist settings state
  settings[isPersistent] = MAX_TIME;
  writeState.settings = settings;

  // persist settings to the backend when they change (but debounced to reduce load on the backend)
  unsubscribeSettings = subscribeKey(
    readState,
    "settings",
    subscriptionStateDiff(
      debouncedPersistSettingsOnChange(),
      readState.settings,
    ),
  );
}

/**
 * When settings change, persist them to the backend. We debounce
 * this to avoid spamming the backend.
 */
export function debouncedPersistSettingsOnChange() {
  // debounce persisting preferences to the backend to prevent spamming calls
  let debounceTimeout;
  // capture the state of settings on the first call so we can run a final diff
  let debouncePrevSettings;

  resetUserPreferenceUpdatePromise();

  // return our debounced subscription handler
  return (nextSettings, prevSettings) => {
    if (!readState.user) return;

    if (!debouncePrevSettings) {
      debouncePrevSettings = prevSettings;
    }

    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(
      () => {
        const changedUserPreferences = diff(debouncePrevSettings, nextSettings);

        debouncePrevSettings = null;

        if (changedUserPreferences) {
          resolveUserPreferenceUpdatePromise(
            updateUserPreferences(changedUserPreferences),
          );
        }
      },
      IS_NODE ? 0 : 1.5 * 1000,
    );
  };
}

type BlitzApp = User["preferences"]["config"]["blitzApp"];

/**
 * Deserializes settings that were persisted to the backend and
 * merges them into the given settings. This method mutates the
 * settings object.
 */
async function mergePersistedSettings(
  blitzApp: BlitzApp,
  settings: AppSettings,
) {
  const persistedSettings: BlitzApp = await deserializeValue(blitzApp);

  if (!persistedSettings) return;

  if (persistedSettings.loggedInAccounts) {
    persistedSettings.loggedInAccounts = migrateLinkedAccounts(
      persistedSettings.loggedInAccounts,
    );
    // because BE transforms all keys to snake case then back to camel csgo ids are messed
    if (persistedSettings.loggedInAccounts.cs2) {
      persistedSettings.loggedInAccounts.cs2 = Object.values(
        persistedSettings.loggedInAccounts.cs2,
      ).reduce(
        (acc: BlitzApp["loggedInAccounts"]["cs2"], account: CsgoPlayer) => {
          if (account.steamId) acc[account.steamId] = account;
          return acc;
        },
        {},
      );
    }
  }

  if (persistedSettings.lastLoggedInIdByGame) {
    persistedSettings.lastLoggedInIdByGame = migrateLinkedAccounts(
      persistedSettings.lastLoggedInIdByGame,
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const changes: any = diff(settings, persistedSettings, {
    ignoreMissingKeys: true,
  });

  // handles an old bug where non-unique values in the favorite games array were persisted to backend
  if (changes?.favoriteGames) {
    changes.favoriteGames = [...new Set(changes.favoriteGames)];
  }

  if (!changes) return;

  deepMerge(settings, changes);
}
